import { Signal, createSignal } from 'solid-js';
import { Patch, SpicePatch } from '.';
import { Patcher, StandardPatcher, ToolTip, UnionPatcher, AssemblerPatcher, Splitter } from './patch-types';

export class PatcherSet {
    mods: Patcher[] = [];

    validPatches: Signal<number>;

    dllFile: Signal<Uint8Array | undefined>;
    errorLog: Signal<string>;

    validated: Signal<boolean>;
    visible: Signal<boolean>;

    constructor(
        public readonly filename: string,
        public readonly gameCode: string,
        public readonly dateCode: number,
        public readonly description: string,
        args: Patch[]
    ) {
        let baseAddr = 0;

        for (const mod of args) {
            // TODO: other sections?
            if (mod.type == 'base') {
                baseAddr = mod.base;
            } else if (!mod.type) {
                this.mods.push(new StandardPatcher(mod, baseAddr));
            } else if (mod.type === 'asm') {
                this.mods.push(new AssemblerPatcher(mod, baseAddr));
            } else if (mod.type === 'union') {
                this.mods.push(new UnionPatcher(mod, baseAddr));
            } else if (mod.type === 'tooltip') {
                this.mods.push(new ToolTip(mod));
            } else if (mod.type === 'splitter') {
                this.mods.push(new Splitter());
            }

            if (mod.type && mod.child) {
                for (const c of mod.child) {
                    if (!c.type) {
                        this.mods.push(new StandardPatcher(c, baseAddr));
                    } else if (c.type === 'asm') {
                        this.mods.push(new AssemblerPatcher(c, baseAddr));
                    } else if (c.type === 'union') {
                        this.mods.push(new UnionPatcher(c, baseAddr));
                    }
                }
            }
        }

        this.validated = createSignal(false);
        this.visible = createSignal(false);
        this.validPatches = createSignal(0);
        this.errorLog = createSignal('');
        this.dllFile = createSignal(undefined) as Signal<Uint8Array | undefined>;
    }

    get name(): string {
        return `${this.gameCode} ${this.dateCode} [${this.description}]`;
    }

    get totalPatches(): number {
        return this.mods.filter(v => !v.uiOnly).length;
    }

    loadBuffer(buffer: ArrayBuffer | null) {
        const [__, setDllFile] = this.dllFile;

        if (!buffer) {
            throw new Error('file not loaded');
        }

        setDllFile(new Uint8Array(buffer));
    }

    unload() {
        const [_, setDllFile] = this.dllFile;
        const [__, setValidPatches] = this.validPatches;
        const [___, setVisible] = this.visible;
        const [____, setValidated] = this.validated;

        setDllFile(undefined);
        setValidPatches(0);
        setVisible(false);
        setValidated(false);
    }

    loadFile(file: Blob) {
        const reader = new FileReader();

        reader.onload = (e) => {
            if (!e?.target) return;

            this.loadBuffer(e.target.result as ArrayBuffer | null);
        };

        reader.readAsArrayBuffer(file);
    }

    downloadURI(uri: string, filename: string) {
        // http://stackoverflow.com/a/18197341
        const element = document.createElement('a');
        element.href = uri;
        element.download = filename;
        element.type = 'application/vnd.microsoft.portable-executable';

        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
    }

    saveDll() {
        const [dllFile] = this.dllFile;
        const buffer = dllFile();

        if (!buffer || !this.mods || !this.filename)
            return;

        for (const mod of this.mods) {
            mod.applyPatch(buffer);
        }

        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        const uri = URL.createObjectURL(blob);
        this.downloadURI(uri, this.filename);
        URL.revokeObjectURL(uri);
    }

    exportPatches() {
        const patches: SpicePatch[] = this.mods
            .map(v => v.exportPatch(this.gameCode, this.dateCode, this.filename)!)
            .filter(v => v);

        const blob = new Blob([JSON.stringify(patches)], { type: 'application/json' });
        const uri = URL.createObjectURL(blob);
        this.downloadURI(uri, 'patches.json');
        URL.revokeObjectURL(uri);
    }

    exportPatches2X(name: string) {
        const patches: SpicePatch[] = this.mods
            .map(v => v.exportPatch2X(this.gameCode, this.dateCode, this.filename)!)
            .filter(v => v);

        const blob = new Blob([JSON.stringify(patches)], { type: 'application/json' });
        const uri = URL.createObjectURL(blob);
        this.downloadURI(uri, name);
        URL.revokeObjectURL(uri);
    }

    validatePatches(): boolean {
        const [dllFile] = this.dllFile;
        const buffer = dllFile();

        if (!buffer) {
            throw new Error('not loaded!');
        }

        const [_, setValidPatches] = this.validPatches;
        const [__, setErrorLog] = this.errorLog;
        const [___, setValidated] = this.validated;

        let errorLog = '';
        let validPatches = 0;

        for (const mod of this.mods) {
            const result = mod.validatePatch(buffer);

            if (result.valid) {
                validPatches++;
                continue;
            }

            if (result.message) {
                errorLog += result.message + '<br/>';
            }

            continue;
        }

        setErrorLog(errorLog);
        setValidPatches(validPatches);

        return setValidated(validPatches === this.totalPatches);
    }
}
